import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  Image,
  X,
  Search,
  FileText,
  CheckCircle,
  Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FraudCasesAdminPage({ user, onLogout, refreshUserData }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGalleryDialog, setShowGalleryDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [submitting, setSubmitting] = useState(false);
  
  // File upload refs
  const coverImageRef = useRef(null);
  const galleryImageRef = useRef(null);

  useEffect(() => {
    // Check if user is super_admin
    if (!user || user.role !== "super_admin") {
      navigate("/");
      return;
    }
    fetchCases();
  }, [user, navigate]);

  function getEmptyFormData() {
    return {
      title: "",
      slug: "",
      status: "DRAFT",
      excerpt: "",
      story: "",
      source_type: "OTHER",
      source_name: "",
      source_url: "",
      source_button_label: "Voir la source originale",
      location: "",
      fraud_type: "none",
      amount_lost: "",
      risk_level: "none",
      tags: "",
      meta_title: "",
      meta_description: "",
    };
  }

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/fraud-cases/admin`);
      setCases(response.data.cases || []);
      setStats(response.data.stats || { total: 0, published: 0, draft: 0 });
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Erreur lors du chargement des cas");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        fraud_type: formData.fraud_type === "none" ? null : formData.fraud_type,
        amount_lost: formData.amount_lost ? parseInt(formData.amount_lost) : null,
        risk_level: formData.risk_level && formData.risk_level !== "none" ? parseInt(formData.risk_level) : null,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      
      await axios.post(`${API}/fraud-cases/admin`, payload);
      toast.success("Cas créé avec succès");
      setShowCreateDialog(false);
      setFormData(getEmptyFormData());
      fetchCases();
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error("Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        fraud_type: formData.fraud_type === "none" ? null : formData.fraud_type,
        amount_lost: formData.amount_lost ? parseInt(formData.amount_lost) : null,
        risk_level: formData.risk_level && formData.risk_level !== "none" ? parseInt(formData.risk_level) : null,
        tags: typeof formData.tags === 'string' 
          ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) 
          : formData.tags,
      };
      
      await axios.put(`${API}/fraud-cases/admin/${selectedCase.id}`, payload);
      toast.success("Cas mis à jour avec succès");
      setShowEditDialog(false);
      setSelectedCase(null);
      setFormData(getEmptyFormData());
      fetchCases();
    } catch (error) {
      console.error("Error updating case:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/fraud-cases/admin/${selectedCase.id}`);
      toast.success("Cas supprimé avec succès");
      setShowDeleteDialog(false);
      setSelectedCase(null);
      fetchCases();
    } catch (error) {
      console.error("Error deleting case:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCase) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/fraud-cases/admin/${selectedCase.id}/cover-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Image de couverture téléversée");
      fetchCases();
      // Update selected case
      setSelectedCase(prev => ({ ...prev, cover_image_url: response.data.url }));
    } catch (error) {
      console.error("Error uploading cover image:", error);
      toast.error("Erreur lors du téléversement");
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCase) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', selectedCase.title);
    
    try {
      const response = await axios.post(
        `${API}/fraud-cases/admin/${selectedCase.id}/gallery`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success("Image ajoutée à la galerie");
      // Update selected case images
      setSelectedCase(prev => ({
        ...prev,
        images: [...(prev.images || []), response.data.image]
      }));
      fetchCases();
    } catch (error) {
      console.error("Error uploading gallery image:", error);
      toast.error("Erreur lors du téléversement");
    }
  };

  const handleRemoveGalleryImage = async (imageId) => {
    if (!selectedCase) return;
    
    try {
      await axios.delete(`${API}/fraud-cases/admin/${selectedCase.id}/gallery/${imageId}`);
      toast.success("Image retirée de la galerie");
      setSelectedCase(prev => ({
        ...prev,
        images: prev.images.filter(img => img.id !== imageId)
      }));
      fetchCases();
    } catch (error) {
      console.error("Error removing gallery image:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (caseItem) => {
    setSelectedCase(caseItem);
    setFormData({
      title: caseItem.title || "",
      slug: caseItem.slug || "",
      status: caseItem.status || "DRAFT",
      excerpt: caseItem.excerpt || "",
      story: caseItem.story || "",
      source_type: caseItem.source_type || "OTHER",
      source_name: caseItem.source_name || "",
      source_url: caseItem.source_url || "",
      source_button_label: caseItem.source_button_label || "Voir la source originale",
      location: caseItem.location || "",
      fraud_type: caseItem.fraud_type || "none",
      amount_lost: caseItem.amount_lost?.toString() || "",
      risk_level: caseItem.risk_level?.toString() || "none",
      tags: Array.isArray(caseItem.tags) ? caseItem.tags.join(", ") : "",
      meta_title: caseItem.meta_title || "",
      meta_description: caseItem.meta_description || "",
    });
    setShowEditDialog(true);
  };

  const openGalleryDialog = (caseItem) => {
    setSelectedCase(caseItem);
    setShowGalleryDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-CA");
  };

  const filteredCases = cases.filter((c) => {
    const matchesSearch =
      c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const fraudTypeOptions = [
    { value: "none", label: "Non spécifié" },
    { value: "revente_billets", label: "Revente de billets" },
    { value: "faux_billets", label: "Faux billets" },
    { value: "usurpation_identite", label: "Usurpation d'identité" },
    { value: "arnaque_paiement", label: "Arnaque au paiement" },
    { value: "autre", label: "Autre" },
  ];

  const sourceTypeOptions = [
    { value: "JOURNAL", label: "Journal/Média" },
    { value: "FACEBOOK", label: "Facebook" },
    { value: "OTHER", label: "Autre" },
  ];

  const FraudCaseForm = ({ isEdit = false }) => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 border-b pb-2">Informations de base</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Titre *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titre du cas de fraude"
            />
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="auto-généré si vide"
            />
          </div>
          <div>
            <Label>Statut</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAFT">Brouillon</SelectItem>
                <SelectItem value="PUBLISHED">Publié</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 border-b pb-2">Contenu</h3>
        <div>
          <Label>Extrait (résumé court)</Label>
          <Textarea
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            placeholder="Court résumé affiché dans la liste"
            rows={2}
          />
        </div>
        <div>
          <Label>Histoire complète</Label>
          <Textarea
            value={formData.story}
            onChange={(e) => setFormData({ ...formData, story: e.target.value })}
            placeholder="L'histoire détaillée de la fraude..."
            rows={6}
          />
        </div>
      </div>

      {/* Details */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 border-b pb-2">Détails</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Type de fraude</Label>
            <Select
              value={formData.fraud_type}
              onValueChange={(value) => setFormData({ ...formData, fraud_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {fraudTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Localisation</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Ex: Montréal, QC"
            />
          </div>
          <div>
            <Label>Montant perdu ($)</Label>
            <Input
              type="number"
              value={formData.amount_lost}
              onChange={(e) => setFormData({ ...formData, amount_lost: e.target.value })}
              placeholder="Ex: 500"
            />
          </div>
          <div>
            <Label>Niveau de risque (1-5)</Label>
            <Select
              value={formData.risk_level}
              onValueChange={(value) => setFormData({ ...formData, risk_level: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non spécifié</SelectItem>
                <SelectItem value="1">1 - Faible</SelectItem>
                <SelectItem value="2">2 - Modéré</SelectItem>
                <SelectItem value="3">3 - Moyen</SelectItem>
                <SelectItem value="4">4 - Élevé</SelectItem>
                <SelectItem value="5">5 - Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Tags (séparés par des virgules)</Label>
          <Input
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder="Ex: facebook, billets, arnaque"
          />
        </div>
      </div>

      {/* Source */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 border-b pb-2">Source</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Type de source</Label>
            <Select
              value={formData.source_type}
              onValueChange={(value) => setFormData({ ...formData, source_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sourceTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nom de la source</Label>
            <Input
              value={formData.source_name}
              onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
              placeholder="Ex: La Presse, Journal de Montréal"
            />
          </div>
          <div className="md:col-span-2">
            <Label>URL de la source</Label>
            <Input
              value={formData.source_url}
              onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <div className="md:col-span-2">
            <Label>Texte du bouton source</Label>
            <Input
              value={formData.source_button_label}
              onChange={(e) => setFormData({ ...formData, source_button_label: e.target.value })}
              placeholder="Voir la source originale"
            />
          </div>
        </div>
      </div>

      {/* SEO */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 border-b pb-2">SEO (optionnel)</h3>
        <div>
          <Label>Titre SEO</Label>
          <Input
            value={formData.meta_title}
            onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
            placeholder="Titre pour les moteurs de recherche"
          />
        </div>
        <div>
          <Label>Description SEO</Label>
          <Textarea
            value={formData.meta_description}
            onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
            placeholder="Description pour les moteurs de recherche"
            rows={2}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              Gestion des Cas de Fraudes
            </h1>
            <p className="text-slate-600 mt-1">Administration du module anti-fraude</p>
          </div>
          <Button onClick={() => { setFormData(getEmptyFormData()); setShowCreateDialog(true); }} className="gap-2">
            <Plus className="h-5 w-5" />
            Nouveau cas
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-slate-100 rounded-lg">
                  <FileText className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-slate-600">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.published}</p>
                  <p className="text-slate-600">Publiés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.draft}</p>
                  <p className="text-slate-600">Brouillons</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="PUBLISHED">Publiés</SelectItem>
                  <SelectItem value="DRAFT">Brouillons</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun cas trouvé</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Localisation</TableHead>
                    <TableHead>Risque</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCases.map((caseItem) => (
                    <TableRow key={caseItem.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {caseItem.cover_image_url ? (
                            <img
                              src={caseItem.cover_image_url}
                              alt=""
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                              <AlertTriangle className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium line-clamp-1">{caseItem.title}</p>
                            <p className="text-sm text-gray-500">/fraudes/{caseItem.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={caseItem.status === "PUBLISHED" ? "default" : "secondary"}
                          className={caseItem.status === "PUBLISHED" ? "bg-green-100 text-green-800" : ""}
                        >
                          {caseItem.status === "PUBLISHED" ? (
                            <><Eye className="h-3 w-3 mr-1" /> Publié</>
                          ) : (
                            <><EyeOff className="h-3 w-3 mr-1" /> Brouillon</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>{caseItem.location || "-"}</TableCell>
                      <TableCell>
                        {caseItem.risk_level ? (
                          <Badge variant="outline">
                            Niveau {caseItem.risk_level}
                          </Badge>
                        ) : "-"}
                      </TableCell>
                      <TableCell>{formatDate(caseItem.publish_date || caseItem.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openGalleryDialog(caseItem)}
                            title="Gérer les images"
                          >
                            <Image className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(caseItem)}
                            title="Modifier"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedCase(caseItem); setShowDeleteDialog(true); }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Nouveau cas de fraude</DialogTitle>
          </DialogHeader>
          <FraudCaseForm />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Modifier le cas de fraude</DialogTitle>
          </DialogHeader>
          <FraudCaseForm isEdit />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleEdit} disabled={submitting}>
              {submitting ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Êtes-vous sûr de vouloir supprimer le cas "{selectedCase?.title}" ?
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gallery Dialog */}
      <Dialog open={showGalleryDialog} onOpenChange={setShowGalleryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gérer les images - {selectedCase?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Cover Image */}
            <div>
              <Label className="text-base font-semibold">Image de couverture</Label>
              <div className="mt-2 flex items-start gap-4">
                {selectedCase?.cover_image_url ? (
                  <img
                    src={selectedCase.cover_image_url}
                    alt="Cover"
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Image className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    ref={coverImageRef}
                    onChange={handleCoverImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => coverImageRef.current?.click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {selectedCase?.cover_image_url ? "Remplacer" : "Téléverser"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Gallery */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Galerie</Label>
                <input
                  type="file"
                  ref={galleryImageRef}
                  onChange={handleGalleryImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => galleryImageRef.current?.click()}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter
                </Button>
              </div>
              {selectedCase?.images?.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {selectedCase.images.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.url}
                        alt={img.alt || ""}
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleRemoveGalleryImage(img.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Aucune image dans la galerie</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowGalleryDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
